import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { GetEventDemographicsStatsQuery as GetEventDemographicsStats } from '__generated__/graphql';
import { assetUrl } from '../../common/helpers';
import OriginList from './OriginList';
import UI from '../../common/UI';

type Event = GetEventDemographicsStats['event'];

interface GeoStatsProps {
  event: Event;
}

const GeoStats = memo(({ event }: GeoStatsProps) => {
  const { t } = useTranslation();

  return (
    <UI.GridContainer sc={{ columns: ['1fr', '1fr 1fr 1fr'] }}>
      <OriginList
        title={t('origin_by_country')}
        items={event.analytics.countries}
        itemsCount={event.analytics.countries.filter(({ country }) => country !== null).length}
        totalCount={event.registrations_count}
        renderItem={(item) => (
          <>
            <UI.Span
              sc={{ background: 'gray.300', inlineBlock: true, mr: 1 }}
              style={{
                width: 12, height: 9, outline: '1px solid rgba(255, 255, 255, .5)', borderRadius: 1, marginBottom: 1,
              }}
            >
              {item.country && (
                <img
                  src={assetUrl(`images/flags/${item.country}.svg`)}
                  alt={t(`common:countries.${item.country}`)}
                  title={t(`common:countries.${item.country}`)}
                  style={{ display: 'block', width: 12, height: 9, borderRadius: 1 }}
                />
              )}
            </UI.Span>
            {item.country
              ? t(`common:countries.${item.country}`)
              : <UI.Span sc={{ muted: true }}>{t('unknown')}</UI.Span>}
          </>
        )}
        image="dashboard-countries.svg"
      />

      <OriginList
        title={t('origin_by_state')}
        items={event.analytics.states}
        itemsCount={event.analytics.states.filter(({ state }) => state !== null).length}
        totalCount={event.registrations_count}
        renderItem={(item) => (
          <>
            <UI.Span
              sc={{ background: 'gray.300', inlineBlock: true, mr: 1 }}
              style={{
                width: 12, height: 9, outline: '1px solid rgba(255, 255, 255, .5)', borderRadius: 1, marginBottom: 1,
              }}
            >
              {item.country && (
                <img
                  src={assetUrl(`images/flags/${item.country}.svg`)}
                  alt={t(`common:countries.${item.country}`)}
                  title={t(`common:countries.${item.country}`)}
                  style={{ display: 'block', width: 12, height: 9, borderRadius: 1 }}
                />
              )}
            </UI.Span>
            {item.state || <UI.Span sc={{ muted: true }}>{t('unknown')}</UI.Span>}
          </>
        )}
        image="dashboard-states.svg"
      />

      <OriginList
        title={t('origin_by_city')}
        items={event.analytics.cities}
        itemsCount={event.analytics.cities.filter(({ city }) => city !== null).length}
        totalCount={event.registrations_count}
        renderItem={(item) => (
          <>
            <UI.Span
              sc={{ background: 'gray.300', inlineBlock: true, mr: 1 }}
              style={{
                width: 12, height: 9, outline: '1px solid rgba(255, 255, 255, .5)', borderRadius: 1, marginBottom: 1,
              }}
            >
              {item.country && (
                <img
                  src={assetUrl(`images/flags/${item.country}.svg`)}
                  alt={t(`common:countries.${item.country}`)}
                  title={t(`common:countries.${item.country}`)}
                  style={{ display: 'block', width: 12, height: 9, borderRadius: 1 }}
                />
              )}
            </UI.Span>
            {item.city || <UI.Span sc={{ muted: true }}>{t('unknown')}</UI.Span>}
          </>
        )}
        image="dashboard-cities.svg"
      />
    </UI.GridContainer>
  );
});

export default GeoStats;
